import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { FiArrowLeft, FiTool } from "react-icons/fi";
import ProductionReadyTable from "./components/ProductionReadyTable";
import {
    getProductionTableData,
    getAvailableUsers
} from "../../lib/supabase/schedule";
import {
    subscribeToJobUpdates,
    unsubscribeFromJobUpdates,
    subscribeToProductionUpdates
} from "../../lib/supabase/realtime";

/**
 * ProductionSchedule Component
 * Handles schedule view for production assignments
 */
const ProductionSchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const [productionData, setProductionData] = useState([]);
    const [availableUsers, setAvailableUsers] = useState({ designers: [], estimators: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');

    const [productionPagination, setProductionPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        hasMore: true
    });
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        if (company?.id) {
            fetchData();
        }
    }, [company, showOnlyMine, selectedUser]);

    useEffect(() => {
        if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
            fetchAvailableUsers();
        }
    }, [userProfile, company]);

    const fetchAvailableUsers = async () => {
        if (!company?.id) return;

        try {
            const users = await getAvailableUsers(company.id);
            setAvailableUsers(users);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        }
    };

    const fetchData = async (loadMore = false) => {
        if (!company?.id) return;

        try {
            if (!loadMore) {
                setIsLoading(true);
            }

            const currentPage = loadMore ? productionPagination.page + 1 : 1;

            const filters = {
                userId: showOnlyMine ? userProfile?.id : selectedUser || null
            };

            if (loadMore) setIsLoadingMore(true);

            const result = await getProductionTableData(
                company.id,
                'production',
                currentPage,
                productionPagination.limit,
                filters
            );

            if (loadMore) {
                setProductionData(prev => [...prev, ...result.data]);
            } else {
                setProductionData(result.data);
            }

            setProductionPagination(result.pagination);

            if (loadMore) setIsLoadingMore(false);
        } catch (error) {
            console.error("Error fetching production data:", error);
        } finally {
            if (!loadMore) {
                setIsLoading(false);
            }
        }
    };


    const loadMoreProductionAssignments = () => {
        if (productionPagination.hasMore && !isLoadingMore) {
            fetchData(true);
        }
    };


    useEffect(() => {
        if (!company?.id) return;

        console.log('Setting up real-time subscriptions');

        // Handle job table updates
        const handleJobUpdate = (payload) => {
            console.log('Job update received:', payload);

            if (payload.eventType === 'DELETE') {
                setProductionData(prev => prev.filter(job => job._id !== payload.old.id));
                return;
            }

            if (payload.eventType === 'INSERT') {
                fetchSingleJob(payload.new.id);
                return;
            }

            if (payload.eventType === 'UPDATE') {
                setProductionData(prev => prev.map(job => {
                    if (job._id === payload.new.id) {
                        return {
                            ...job,
                            uniqueId: payload.new.unique_id,
                            customerName: payload.new.customer_name,
                            customerNumber: payload.new.customer_number,
                            customerMail: payload.new.customer_mail,
                            carpenterSupervisorName: payload.new.carpenter_supervisor_name,
                            carpenterSupervisorNumber: payload.new.carpenter_supervisor_number,
                            customerAddress: payload.new.customer_address,
                            jobType: payload.new.job_type,
                            mainStatus: payload.new.main_status,
                            currentStatus: payload.new.current_status,
                            invoiced: payload.new.invoiced,
                            bookingDeliveryDate: payload.new.booking_delivery_date,
                            expectedEstimationFinishDate: payload.new.expected_estimation_finish_date,
                            updatedAt: payload.new.updated_at,
                            jobInformation: {
                                roofType: payload.new.roof_type,
                                roofTrussCentres: payload.new.roof_truss_centres,
                                isSingleStorey: payload.new.is_single_storey,
                                isUnit: payload.new.is_unit,
                                unitCount: payload.new.unit_count,
                                urgentQuote: payload.new.urgent_quote,
                                urgentDesign: payload.new.urgent_design,
                                siteMeasureRequired: payload.new.site_measure_required,
                                siteMeasureWall: payload.new.site_measure_wall,
                                siteMeasureFloor: payload.new.site_measure_floor,
                                siteMeasureTruss: payload.new.site_measure_truss
                            }
                        };
                    }
                    return job;
                }));
            }
        };

        // Handle job_production table updates
        const handleProductionUpdate = (payload) => {
            console.log('Production update received:', payload);

            const { new: newData, old: oldData } = payload;

            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                setProductionData(prev => prev.map(job => {
                    // Find the job that has this production record
                    if (job._id === newData.job_id) {
                        // Find which quote type this production belongs to
                        const quoteType = newData.quote_type;

                        return {
                            ...job,
                            quotes: {
                                ...job.quotes,
                                [quoteType]: {
                                    ...job.quotes?.[quoteType],
                                    production: {
                                        id: newData.id,
                                        userId: newData.user_id,
                                        name: newData.name,
                                        phase: newData.phase,
                                        isStartedCutting: newData.is_started_cutting,
                                        isStartedProduction: newData.is_started_production,
                                        isCompleted: newData.is_completed,
                                        startCuttingAt: newData.start_cutting_at,
                                        endCuttingAt: newData.end_cutting_at,
                                        startProductionAt: newData.start_production_at,
                                        endProductionAt: newData.end_production_at,
                                        productionNotes: newData.production_notes
                                    }
                                }
                            }
                        };
                    }
                    return job;
                }));
            }
        };

        const fetchSingleJob = async (jobId) => {
            try {
                const result = await getProductionTableData(
                    company.id,
                    'production',
                    1,
                    1,
                    { jobId }
                );

                if (result.data.length > 0) {
                    setProductionData(prev => [result.data[0], ...prev]);
                }
            } catch (error) {
                console.error('Failed to fetch single job:', error);
            }
        };

        // Subscribe to both tables
        const jobSubscription = subscribeToJobUpdates(company.id, handleJobUpdate);
        const productionSubscription = subscribeToProductionUpdates(company.id, handleProductionUpdate);

        return () => {
            console.log('Cleaning up real-time subscriptions');
            unsubscribeFromJobUpdates(jobSubscription);
            unsubscribeFromJobUpdates(productionSubscription); // Uses same unsubscribe function
        };
    }, [company?.id]);


    return (
        <>
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={() => navigate(`/${companySlug}/schedule`)}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                >
                    <FiArrowLeft className="w-4 h-4" />
                    <span className="text-sm font-medium">Back to Schedules</span>
                </button>

                <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg">
                        <FiTool className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Production Schedule</h1>
                        <p className="text-gray-600 mt-1">
                            Manage and track production assignments and progress
                        </p>
                    </div>
                </div>
            </div>

            {/* User Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="showOnlyMine"
                            checked={showOnlyMine}
                            onChange={(e) => {
                                setShowOnlyMine(e.target.checked);
                                if (e.target.checked) {
                                    setSelectedUser('');
                                }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="showOnlyMine" className="ml-2 text-sm text-gray-700 font-medium">
                            Show only my assignments
                        </label>
                    </div>

                    {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-700 font-medium">Filter by user:</label>
                            <select
                                value={selectedUser}
                                onChange={(e) => {
                                    setSelectedUser(e.target.value);
                                    if (e.target.value) {
                                        setShowOnlyMine(false);
                                    }
                                }}
                                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500 min-w-[200px] bg-white"
                            >
                                <option value="">All users</option>

                                {availableUsers.designers && availableUsers.designers.length > 0 && (
                                    <optgroup label="Designers">
                                        {availableUsers.designers.map(user => (
                                            <option key={user.userId} value={user.userId}>
                                                {user.username}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}

                                {availableUsers.estimators && availableUsers.estimators.length > 0 && (
                                    <optgroup label="Estimators">
                                        {availableUsers.estimators.map(user => (
                                            <option key={user.userId} value={user.userId}>
                                                {user.username}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Production Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <ProductionReadyTable
                    assignments={productionData}
                    userType={userProfile?.role}
                    company={companySlug}
                    navigate={navigate}
                    onLoadMore={loadMoreProductionAssignments}
                    hasMore={productionPagination.hasMore}
                    isLoadingMore={isLoadingMore}
                    isLoading={isLoading}
                    availableDesigners={availableUsers.designers || []}
                    scheduleType="production"
                    showOnlyMine={showOnlyMine}
                    selectedUser={selectedUser}
                />
            </div>
        </>
    );
};

export default ProductionSchedule;